/**
 * O histórico da organização em frases, sem tela.
 *
 * O servidor manda o que aconteceu com antes e depois; a frase é daqui. A
 * troca só de plano vira "Plano trocado", e não "Contrato alterado", porque é
 * assim que a equipe fala dela. Valores em dinheiro saem com centavos: o
 * histórico é onde se confere o preço, e "R$ 55" não diz se era R$ 54,90.
 */

import type { NomeDoIcone } from '@/shared/ui';
import { diaMesAno, dinheiroComCentavos } from '@/shared/lib/formato';
import { mascararCnpj } from '@/shared/lib/cnpj';
import {
  fusoEmPalavras,
  segmentoDe,
  type Dinheiro,
  type EventoDaOrganizacao,
  type MudancaNoCadastro,
  type MudancaNoContrato,
} from '../model/organizacao-detalhada';

const SEM_VALOR = 'sem valor';

const preco = (valor: Dinheiro | null): string => dinheiroComCentavos(valor) ?? SEM_VALOR;

/** Uma mudança numa linha: "Usuários contratados: 5 → 7". */
export function linhaDaMudanca(mudanca: MudancaNoContrato): string {
  switch (mudanca.campo) {
    case 'plano':
      return `Plano: ${mudanca.antes ?? 'fora do catálogo'} → ${mudanca.depois ?? 'fora do catálogo'}`;
    case 'inicio':
      return `Início do contrato: ${diaMesAno(`${mudanca.antes}T12:00:00.000Z`)} → ${diaMesAno(`${mudanca.depois}T12:00:00.000Z`)}`;
    case 'mensalidade':
      return `Mensalidade: ${preco(mudanca.antes)} → ${preco(mudanca.depois)}`;
    case 'usuarios_contratados':
      return `Usuários contratados: ${mudanca.antes} → ${mudanca.depois}`;
    case 'usuario_adicional':
      return `Usuário adicional: ${preco(mudanca.antes)} → ${preco(mudanca.depois)}`;
    case 'armazenamento_gb':
      return `Armazenamento contratado: ${mudanca.antes} GB → ${mudanca.depois} GB`;
    case 'gb_adicional':
      return `GB adicional: ${preco(mudanca.antes)} → ${preco(mudanca.depois)}`;
  }
}

const NAO_INFORMADO = 'não informado';

/**
 * Uma mudança do cadastro numa linha: "CNPJ: não informado → 11.222.333/0001-81".
 * O contato financeiro não tem valor no histórico: é dado pessoal.
 */
export function linhaDoCadastro(mudanca: MudancaNoCadastro): string {
  switch (mudanca.campo) {
    case 'razao_social':
      return `Razão social: ${mudanca.antes ?? NAO_INFORMADO} → ${mudanca.depois ?? NAO_INFORMADO}`;
    case 'cnpj': {
      const cnpj = (valor: string | null) => (valor ? mascararCnpj(valor) : NAO_INFORMADO);
      return `CNPJ: ${cnpj(mudanca.antes)} → ${cnpj(mudanca.depois)}`;
    }
    case 'segmento': {
      const nome = (valor: typeof mudanca.antes) => segmentoDe(valor)?.nome ?? NAO_INFORMADO;
      return `Segmento: ${nome(mudanca.antes)} → ${nome(mudanca.depois)}`;
    }
    case 'fuso': {
      const fuso = (valor: string | null) => (valor ? fusoEmPalavras(valor) : NAO_INFORMADO);
      return `Fuso horário: ${fuso(mudanca.antes)} → ${fuso(mudanca.depois)}`;
    }
    case 'contato_financeiro':
      return {
        incluido: 'Contato financeiro incluído',
        trocado: 'Contato financeiro trocado',
        removido: 'Contato financeiro removido',
      }[mudanca.acao];
  }
}

/** A frase, o ícone e as linhas de apoio de um acontecimento. */
export function fraseDoEvento(evento: EventoDaOrganizacao): {
  frase: string;
  icone: NomeDoIcone;
  detalhes: string[];
} {
  switch (evento.tipo) {
    case 'criada':
      return { frase: 'Organização criada', icone: 'criar', detalhes: [] };
    case 'contrato_criado':
      return {
        frase: evento.plano
          ? `Contrato criado a partir do plano ${evento.plano}`
          : 'Contrato criado',
        icone: 'lapis',
        detalhes: [],
      };
    case 'contrato_alterado': {
      const soPlano = evento.mudancas.every((mudanca) => mudanca.campo === 'plano');
      return {
        frase: soPlano ? 'Plano trocado' : 'Contrato alterado',
        icone: 'lapis',
        detalhes: evento.mudancas.map(linhaDaMudanca),
      };
    }
    case 'cadastro_alterado':
      return {
        frase: 'Cadastro alterado',
        icone: 'lapis',
        detalhes: evento.mudancas.map(linhaDoCadastro),
      };
    case 'encerrada':
      return { frase: 'Organização encerrada', icone: 'alerta', detalhes: [] };
    case 'reaberta':
      return { frase: 'Organização reaberta', icone: 'repetir', detalhes: [] };
    case 'excluida':
      return { frase: 'Organização excluída', icone: 'lixeira', detalhes: [] };
    case 'usuario_adicional_contratado':
      return {
        frase: 'Usuário adicional contratado pela organização',
        icone: 'lapis',
        detalhes: [
          `Usuários contratados: ${evento.usuarios_contratados.antes} → ${evento.usuarios_contratados.depois}`,
          `Usuário adicional: ${preco(evento.usuario_adicional)} por mês`,
        ],
      };
    case 'armazenamento_adicional_contratado':
      return {
        frase: 'Armazenamento adicional contratado pela organização',
        icone: 'lapis',
        detalhes: [
          `Armazenamento contratado: ${evento.armazenamento_gb.antes} GB → ${evento.armazenamento_gb.depois} GB`,
          `GB adicional: ${preco(evento.gb_adicional)} por mês`,
        ],
      };
  }
}

const HORA = new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' });

/** "agora" no primeiro minuto, "hoje, 14:32" no mesmo dia, e a data depois disso. */
export function quandoEmPalavras(iso: string, agora: number): string {
  const instante = new Date(iso);
  if (agora - instante.getTime() < 60_000) return 'agora';
  const dia = diaMesAno(iso);
  if (dia === diaMesAno(new Date(agora).toISOString())) return `hoje, ${HORA.format(instante)}`;
  return dia;
}

/** Quem fez, em frase: o sistema não tem nome. */
export function porQuem(nome: string | null): string {
  return nome ? `por ${nome}` : 'pelo sistema';
}
